/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep" />
import * as React from "react";
import { VssComponent, IVssComponentContext, css } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize, IStatusProps } from "VSSUI/Status";
import {
    IFirstRunExperienceStepProps,
    StepStatus
} from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStep.Props";
import { NumberOne, NumberTwo } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceStepStatus";

export interface IFirstRunExperienceStepState {
    status: StepStatus;
}

export class FirstRunExperienceStep extends VssComponent<IFirstRunExperienceStepProps, IFirstRunExperienceStepState> {
    constructor(props: IFirstRunExperienceStepProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            status: StepStatus.NotStarted
        };
    }

    /**
     * Possible status flow from props:
     * NotStarted => Started
     * Once Processing, step manages its status based on process result
     */
    public static getDerivedStateFromProps(
        props: IFirstRunExperienceStepProps,
        state: IFirstRunExperienceStepState
    ): IFirstRunExperienceStepState | null {
        const { status } = state;
        if (status !== StepStatus.NotStarted) {
            // Do not alter step status from props if already started
            return null;
        }

        const { stepId, activeStepId } = props;
        if (activeStepId < stepId) {
            // This means "NotStarted". No state change for that.
            return null;
        }

        // Update state
        return { status: StepStatus.Started };
    }

    public render(): JSX.Element {
        const { status } = this.state;
        return (
            <div className="first-run-experience-step">
                <div className={css("step-header", status === StepStatus.Failed && "failed")}>
                    {this._renderStatus()}
                    {this._renderTitle()}
                </div>
                {this._renderContent()}
                {this._renderFooter()}
                {this._renderError()}
                {this._renderHelpLinks()}
            </div>
        );
    }

    private _renderStatus(): JSX.Element | undefined {
        const { stepId } = this.props;
        const defaultStatusProps = stepId === 1 ? NumberOne : stepId === 2 ? NumberTwo : Statuses.Queued;

        const { status } = this.state;
        let statusProps: IStatusProps;
        switch (status) {
            case StepStatus.Succeeded:
                statusProps = Statuses.Success;
                break;
            default:
                statusProps = defaultStatusProps;
                break;
        }

        const statusSize = StatusSize.xl;

        return <Status className="step-status" {...statusProps} size={statusSize} />;
    }

    private _renderTitle(): JSX.Element | undefined {
        const { onRenderTitle } = this.props;
        const { status } = this.state;
        return <div className="step-title body-m">{onRenderTitle(status)}</div>;
    }

    private _renderContent(): JSX.Element | string | undefined {
        const { status } = this.state;
        if (status === StepStatus.Succeeded || status === StepStatus.Failed) {
            return undefined;
        }

        const { onRenderContent } = this.props;
        return onRenderContent && <div className="step-body">{onRenderContent(status)}</div>;
    }

    private _renderFooter(): JSX.Element | undefined {
        const { actionButtonText, stepProcessingText } = this.props;
        const { status } = this.state;
        const isProcessing = status === StepStatus.Processing;
        const shouldShowFooter = status === StepStatus.Started || isProcessing;

        if (!actionButtonText || !stepProcessingText || !shouldShowFooter) {
            return undefined;
        }

        return (
            <div className="step-footer">
                {isProcessing ? (
                    <>
                        <Spinner className="wait-indicator" size={SpinnerSize.small} />
                        <span className="step-processing-text body-m">{stepProcessingText}</span>
                    </>
                ) : (
                    <Button className="step-action-button" text={actionButtonText} primary={true} onClick={this._onActionButtonClicked} />
                )}
            </div>
        );
    }

    private _renderError(): JSX.Element | string | undefined {
        const { status } = this.state;
        if (status !== StepStatus.Failed) {
            return undefined;
        }

        const { onRenderError } = this.props;
        const errorContent = onRenderError && onRenderError();
        return (
            errorContent && (
                <MessageCard className="steps-error" severity={MessageCardSeverity.Error}>
                    {errorContent}
                </MessageCard>
            )
        );
    }

    private _renderHelpLinks(): JSX.Element | undefined {
        const { onRenderHelpLinks } = this.props;
        const { status } = this.state;
        const helpLinks = onRenderHelpLinks && onRenderHelpLinks(status);
        return helpLinks && <div className="step-help-links">{helpLinks}</div>;
    }

    private readonly _onActionButtonClicked = async (): Promise<void> => {
        const { onActionButtonClicked } = this.props;
        if (!onActionButtonClicked) {
            return;
        }

        this._updateStepStatus(StepStatus.Processing);
        try {
            const resultingStatus = await onActionButtonClicked();
            this._updateStepStatus(resultingStatus);
        } catch {
            this._updateStepStatus(StepStatus.Failed);
        }
    };

    /**
     * Possible status flow from internal status transitions
     * NotStarted => Started
     * Started => Processing
     * Processing => Succeeded | Failed
     */
    private _updateStepStatus(newStatus: StepStatus): void {
        const { status } = this.state;
        switch (status) {
            case StepStatus.NotStarted:
                if (newStatus === StepStatus.Started) {
                    this.setState({ status: newStatus });
                }
                break;
            case StepStatus.Started:
                if (newStatus === StepStatus.Processing) {
                    this.setState({ status: newStatus });
                }
                break;
            case StepStatus.Processing:
                if (newStatus === StepStatus.Succeeded || newStatus === StepStatus.Failed) {
                    this.setState({ status: newStatus });
                }
                break;
        }
    }
}
